import { ISO_3166_1_ALPHA_2_CODES } from "./iso-3166-1-alpha-2";
import { z } from "zod";

export enum EGeoRestrictionScope {
  CAMPAIGN = "CAMPAIGN",
  EXCHANGE = "EXCHANGE",
}

export const GeoRestrictionScopeSchema = z.nativeEnum(EGeoRestrictionScope);

/**
 * ISO 3166-1 alpha-2, stored uppercase so it compares directly against the edge's
 * `cf-ipcountry`. Normalized on write rather than at every read site.
 */
export const CountryCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2}$/, "Country must be an ISO 3166-1 alpha-2 code, e.g. AE")
  .refine((code) => ISO_3166_1_ALPHA_2_CODES.has(code), {
    message: "Unknown country code — use the ISO 3166-1 alpha-2 code (the UK is GB)",
  });

// ====== Response DTO (read) ======

export const GeoRestrictionDtoSchema = z.object({
  id: z.uuid(),
  scope: GeoRestrictionScopeSchema,
  targetId: z.string().min(1),
  countryCodes: z.array(CountryCodeSchema).default([]),
  note: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type GeoRestrictionDto = z.infer<typeof GeoRestrictionDtoSchema>;

export const GetGeoRestrictionsByPageResultSchema = z.object({
  data: z.array(GeoRestrictionDtoSchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
});
export type GetGeoRestrictionsByPageResultDto = z.infer<typeof GetGeoRestrictionsByPageResultSchema>;

// ====== Save DTO (write) ======

/**
 * A restriction with no countries blocks nobody. Rejecting it at the boundary keeps
 * "exists but inert" out of the table, where it would read as an active control.
 */
export const SaveGeoRestrictionDtoSchema = z
  .object({
    id: z.uuid().nullable().optional(),
    scope: GeoRestrictionScopeSchema,
    targetId: z.string().trim().min(1),
    countryCodes: z.array(CountryCodeSchema).min(1, "Select at least one country"),
    note: z.string().trim().max(500).nullable().optional(),
  })
  // Exchange ids are matched against `epochGroups.exchangeIds`, which stores them
  // lowercase. A "Kraken" saved from a picker that ever changes casing would match
  // nothing and leave an inert restriction that reads as active in the table.
  .transform((restriction) =>
    restriction.scope === EGeoRestrictionScope.EXCHANGE
      ? { ...restriction, targetId: restriction.targetId.toLowerCase() }
      : restriction,
  );
export type SaveGeoRestrictionDto = z.infer<typeof SaveGeoRestrictionDtoSchema>;

// ====== Query params ======

export const GetGeoRestrictionsByPageQueryParamsSchema = z.object({
  page: z.coerce.number().optional(),
  size: z.coerce.number().optional(),
});
export type GetGeoRestrictionsByPageQueryParamsDto = z.infer<typeof GetGeoRestrictionsByPageQueryParamsSchema>;

export const DeleteGeoRestrictionQueryParamsSchema = z.object({ geoRestrictionId: z.uuid() });
export type DeleteGeoRestrictionQueryParamsDto = z.infer<typeof DeleteGeoRestrictionQueryParamsSchema>;

export const GeoRestrictionCmsSuccessResponseSchema = z.object({ message: z.string() });
export type GeoRestrictionCmsSuccessResponseDto = z.infer<typeof GeoRestrictionCmsSuccessResponseSchema>;

// ====== Cross-module read shape ======

/**
 * The blocked targets for one country, resolved once per request and applied to every
 * campaign surface (list, detail, entry).
 */
export type BlockedGeoTargets = {
  readonly campaignIds: readonly string[];
  readonly exchangeIds: readonly string[];
};
